import { Badge, Box, Button, Collapse, Grid, IconButton, InputAdornment, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { useEffect, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { tableRowLab } from '../../../style/LapPageStyle'
import useDebounce from '../../../hooks/UseDebounce';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { tableCellNewPrescription } from '../../../style/PatinetFormStyle';
import UseFetchTreatmentTemplateList from '../../../helper/UseFetchTreatmentTemplateList';
import AddTreatmentTemplates from './AddTreatmentTemplates';
import ModalDelete from './ModalDelete';
import { showToast } from '../../../utils/toastUtil';
import { treatmentTemplates } from '../../../connections/deleteApi';
import UpdateTreatmentTemplates from './UpdateTreatmentTemplates';
import { rotateSpinner } from '../../../style/PatientDetailsStyle';

// server-side filters supported by /api/v1/admin/treatments (besides `q`)
const FILTER_FIELDS = [
    { key: "name", label: "Name", placeholder: "Plantar", type: "text" },
    { key: "diagnosis", label: "Diagnosis", placeholder: "anxiety", type: "text" },
    { key: "medical_tests", label: "Medical Tests", placeholder: "URIC ACID", type: "text" },
    { key: "special_instructions", label: "Special Instructions", placeholder: "sport shoes", type: "text" },
    { key: "start_date", label: "Start Date", type: "date" },
    { key: "end_date", label: "End Date", type: "date" },
];

const EMPTY_FILTERS = FILTER_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

const filterInputStyle = {
    "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#F7F7FF" },
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& input": { fontSize: "13px", padding: "9px 12px" },
};

const TreatmentTemplate = () => {

    const isMdUp = useMediaQuery("(min-width:450px)");
    const visiblePageCount = isMdUp ? 3 : 5;
    const [page, setPage] = useState(1);
    const [itemPerPage, setItemPerPage] = useState(10);
    const [newMedicine, setNewMedicine] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [id, setId] = useState(null);
    const [isEdit, setIsEdit] = useState(null);
    const [templateData, setTemplateData] = useState(null);

    // search (live, debounced) — sent as `q`
    const [searchInput, setSearchInput] = useState("");
    const debouncedSearch = useDebounce(searchInput, 800);

    // filter panel: draft is what the user types, applied is what the query uses
    const [showFilters, setShowFilters] = useState(false);
    const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);

    const activeFilterCount = Object.values(appliedFilters).filter(
        (v) => String(v || "").trim() !== ""
    ).length;

    const { data: treatmentList, refetch, isLoading, isFetching } = UseFetchTreatmentTemplateList(
        page,
        itemPerPage,
        { q: debouncedSearch || "", ...appliedFilters }
    );

    // any change to the result set starts from page 1
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, appliedFilters]);

    const handleDraftChange = (key, value) =>
        setDraftFilters((prev) => ({ ...prev, [key]: value }));

    // panel stays open after applying so filters can be tweaked against the results
    const handleApplyFilters = () => {
        setAppliedFilters(draftFilters);
    };

    const handleResetFilters = () => {
        setDraftFilters(EMPTY_FILTERS);
        setAppliedFilters(EMPTY_FILTERS);
        setSearchInput("");
    };

    const totalRecords = treatmentList?.meta?.total_count || 0;
    const totalPage = Math.ceil(totalRecords / itemPerPage);

    const handleDelete = async (id) => {
        setDeleteModal(false);
        const res = await treatmentTemplates(id);
        showToast(res?.status?.message || res?.status?.status, res?.status?.status);
        if (res?.status?.status) {
            refetch(page, itemPerPage);
        }
    };

    const handleEdit = async (templateData) => {
        setIsEdit(true);
        setTemplateData(templateData)
    };

    if (newMedicine) {
        return (
            <AddTreatmentTemplates refetch={refetch} onClose={() => setNewMedicine(false)} />
        )
    }

    if (isEdit) {
        return (
            <UpdateTreatmentTemplates refetch={refetch} onClose={() => setIsEdit(false)} templateData={templateData} setTemplateData={setTemplateData} setIsEdit={setIsEdit} />
        )
    }


    return (
        <Box sx={{ width: "100%", padding: '10px', backgroundColor: '#FFFFFF', px: 1 }}>
            <Box
                mt={1}
                mb={0}
                marginLeft={1}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                position="sticky"
            >
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, alignItems: 'center' }} >
                    <Box>
                        <Typography variant='title_medium' sx={{ fontSize: '18px' }}>Treatment Templates</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, alignItems: 'center' }} >
                        <AddRoundedIcon onClick={() => setNewMedicine(true)} sx={{
                            backgroundColor: newMedicine ? "neutral.tealDark" : "#FFFFFF", p: '4px', width: "24px", color: newMedicine ? "#FFFFFF" : "neutral.teal", borderRadius: "10px", cursor: "pointer", border: '2px solid #007A99',
                            transition: "0.3s ease",
                            "&:hover": {
                                backgroundColor: "#007A99", // teal
                                color: "#FFFFFF",
                            }
                        }} />

                        <IconButton
                            onClick={() => refetch()}
                            sx={{
                                backgroundColor: "#FFFFFF",
                                width: "36px",
                                height: "36px",
                                borderRadius: "10px",
                                cursor: "pointer",
                                border: "2px solid #007A99",
                                transition: "0.3s ease",

                                "&:hover": {
                                    backgroundColor: "neutral.teal",
                                },
                            }}
                        >
                            <CachedOutlinedIcon
                                sx={{
                                    width: "22px",
                                    height: "22px",
                                    color: "neutral.teal",
                                    transition: "0.3s ease",

                                    // white color on parent hover
                                    ".MuiIconButton-root:hover &": {
                                        color: "#FFFFFF",
                                    },
                                    // animation if loading
                                    animation: isFetching ? `${rotateSpinner} 0.9s linear infinite` : "none",
                                }}
                            />
                        </IconButton>

                    </Box>
                </Box>

                {/* right side: search + filter toggle */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center', marginRight: 1 }}>
                    <TextField
                            size="small"
                            placeholder="Search templates"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            sx={{ ...filterInputStyle, width: { xs: "160px", sm: "240px" } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: "18px", color: "#7A8A90" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchInput ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchInput("")}>
                                            <CloseIcon sx={{ fontSize: "15px", color: "#7A8A90" }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />

                        {/* filter toggle — opens the panel above the table */}
                        <Tooltip title={showFilters ? "Hide filters" : "Show filters"} arrow>
                            <Badge
                                badgeContent={activeFilterCount}
                                color="primary"
                                sx={{ "& .MuiBadge-badge": { fontSize: "10px", height: "16px", minWidth: "16px", backgroundColor: "#007A99" } }}
                            >
                                <IconButton
                                    onClick={() => setShowFilters((prev) => !prev)}
                                    sx={{
                                        backgroundColor: showFilters ? "#007A99" : "#FFFFFF",
                                        width: "36px",
                                        height: "36px",
                                        borderRadius: "10px",
                                        border: "2px solid #007A99",
                                        transition: "0.3s ease",
                                        "&:hover": { backgroundColor: "neutral.teal" },
                                    }}
                                >
                                    <FilterAltOutlinedIcon
                                        sx={{
                                            width: "22px",
                                            height: "22px",
                                            color: showFilters ? "#FFFFFF" : "neutral.teal",
                                            ".MuiIconButton-root:hover &": { color: "#FFFFFF" },
                                        }}
                                    />
                                </IconButton>
                            </Badge>
                        </Tooltip>
                </Box>
            </Box>

            {/* ── filter panel ─────────────────────────────────────────────── */}
            <Collapse in={showFilters} unmountOnExit>
                <Box
                    sx={{
                        mt: 2,
                        mx: 1,
                        p: 2,
                        borderRadius: "14px",
                        backgroundColor: "#FBFCFD",
                        border: "1px solid #E3EAED",
                    }}
                >
                    <Grid container spacing={2}>
                        {FILTER_FIELDS.map((field) => (
                            <Grid item xs={12} sm={6} md={4} key={field.key}>
                                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#4A5A60", mb: "4px" }}>
                                    {field.label}
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type={field.type}
                                    placeholder={field.placeholder || ""}
                                    value={draftFilters[field.key]}
                                    onChange={(e) => handleDraftChange(field.key, e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
                                    InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
                                    sx={filterInputStyle}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 2 }}>
                        <Button
                            onClick={handleResetFilters}
                            startIcon={<RestartAltIcon sx={{ fontSize: "16px" }} />}
                            sx={{
                                textTransform: "none",
                                fontSize: "13px",
                                color: "#007A99",
                                border: "1px solid #007A99",
                                borderRadius: "8px",
                                px: 2,
                            }}
                        >
                            Reset
                        </Button>
                        <Button
                            onClick={handleApplyFilters}
                            sx={{
                                textTransform: "none",
                                fontSize: "13px",
                                color: "#FFFFFF",
                                backgroundColor: "#007A99",
                                borderRadius: "8px",
                                px: 3,
                                "&:hover": { backgroundColor: "#006c88" },
                            }}
                        >
                            Apply
                        </Button>
                    </Box>
                </Box>
            </Collapse>
            <Grid item xs={12} mt={3}>
                <TableContainer sx={{ mt: 1, width: "100%" }}>
                    <Table sx={{ width: "100%" }}>
                        <TableHead stickyHeader>
                            <TableRow
                                sx={{
                                    alignItems: 'center',
                                    "& th": {
                                        backgroundColor: "#EDEDED",
                                    },
                                }}
                            >
                                <TableCell sx={{ borderTopLeftRadius: '16px' }}>
                                    <Typography variant="title_small2" color={'#333333'} fontSize={"18px"}>
                                        ID
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography variant="title_small2" color={'#333333'} fontSize={"18px"}>
                                        Name
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography variant="title_small2" color={'#333333'} fontSize={"18px"}>
                                        Diagnosis
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography variant="title_small2" color={'#333333'} fontSize={"18px"}>
                                        Medical Tests
                                    </Typography>
                                </TableCell>

                                <TableCell >
                                    <Typography variant="title_small2" color={'#333333'} fontSize={"18px"}>
                                        Special Instructions
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="title_small2" color={'#333333'} fontSize={"18px"}>
                                        Self Notes
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ borderTopRightRadius: '16px' }}>
                                    <Typography variant="title_small2" color={'#333333'} fontSize={"18px"}>
                                        Actions
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        {isLoading ? (
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={12} sx={{ p: 0 }}>
                                        <Box sx={{ rowGap: 1.2, display: 'flex', flexDirection: 'column' }}>
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]?.map((item, index) =>
                                                <Skeleton key={index}
                                                    variant="rectangular"
                                                    sx={{
                                                        width: "100%",
                                                        height: "50px",
                                                        backgroundColor: "#f0f0f0",
                                                    }}
                                                />
                                            )}

                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )
                            :
                            (
                                <TableBody>
                                    {(treatmentList?.payload?.treatments?.length || 0) === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, border: 'none' }}>
                                                <Typography variant="subTitle116px" sx={{ color: '#8A9AA0' }}>
                                                    {debouncedSearch || activeFilterCount > 0
                                                        ? 'No treatment templates match these filters.'
                                                        : 'No treatment templates yet.'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {treatmentList?.payload?.treatments?.map((medicine, index) =>
                                        <TableRow key={index} sx={{
                                            ...tableRowLab, textAlign: "left", backgroundColor: "neutral.white",
                                            "& td:last-of-type": {
                                                borderTopRightRadius: "12px",
                                                borderBottomRightRadius: "12px",
                                                textAlign: "right",
                                            },
                                        }}>
                                            <TableCell
                                                sx={{
                                                    ...tableCellNewPrescription,
                                                    borderTopLeftRadius: '20px',
                                                    borderBottomLeftRadius: '20px',
                                                }}
                                            >
                                                <Typography variant="subTitle116px" sx={{ color: "#3A3A3A" }}> {medicine?.id} </Typography>
                                            </TableCell>

                                            <TableCell
                                                sx={tableCellNewPrescription}
                                            >
                                                <Typography
                                                    variant="subTitle116px"
                                                    sx={{
                                                        cursor: "pointer",
                                                        display: "inline-block",
                                                        py: 1,
                                                    }}
                                                >
                                                    {medicine?.name}
                                                </Typography>
                                            </TableCell>

                                            <TableCell
                                                sx={tableCellNewPrescription}
                                            >
                                                <Typography
                                                    variant="subTitle116px"
                                                    sx={{
                                                        color: "#3A3A3A",
                                                        py: 1,
                                                    }}
                                                >
                                                    {medicine?.diagnosis?.toString()}
                                                </Typography>
                                            </TableCell>

                                            <TableCell
                                                sx={tableCellNewPrescription}
                                            >
                                                <Typography variant="subTitle116px" sx={{ color: "#3A3A3A" }}> {medicine?.medical_tests?.toString()}</Typography>
                                            </TableCell>

                                            <TableCell
                                                sx={tableCellNewPrescription}
                                            >
                                                <Typography variant="subTitle116px" sx={{ color: "#3A3A3A" }}> {medicine?.special_instructions?.toString()}</Typography>
                                            </TableCell>

                                            <TableCell
                                                sx={tableCellNewPrescription}
                                            >
                                                <Typography variant="subTitle116px" sx={{ color: "#3A3A3A" }}> {medicine?.self_notes}</Typography>
                                            </TableCell>


                                            <TableCell
                                                sx={{
                                                    ...tableCellNewPrescription,
                                                    width: '30px',
                                                    borderTopRightRadius: "20px",
                                                    borderBottomRightRadius: '20px',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', gap: '1px', justifyContent: 'center' }}>
                                                    <IconButton>
                                                        <EditNoteOutlinedIcon onClick={() => handleEdit(medicine)}
                                                            sx={{ width: "24px", color: '#4A4A4A', }} />
                                                    </IconButton>
                                                    {/* <IconButton onClick={() => {
                                                        setDeleteModal(true);
                                                        setId(medicine?.id);
                                                    }}>
                                                        <DeleteForeverOutlinedIcon
                                                            sx={{ width: "24px", color: '#FB3748', }}
                                                        />
                                                    </IconButton> */}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            )}
                    </Table>
                </TableContainer>
            </Grid>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 0px",
                    backgroundColor: "#fff",
                    flexWrap: 'wrap'
                }}
            >

                <Typography fontSize="14px">
                    Showing {(page - 1) * itemPerPage + 1} -
                    {Math.min(page * itemPerPage, totalRecords)} of {totalRecords} entries
                </Typography>

                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>

                    {/* Previous Button */}
                    <Button
                        onClick={() => page > 1 && setPage(page - 1)}
                        startIcon={<ArrowBackIcon sx={{ fontSize: "14px" }} />}
                        disabled={page === 1}
                        sx={{
                            textTransform: "none",
                            fontSize: "12px",
                            color: page === 1 ? "#9e9e9e" : "#007A99",
                            "&:disabled": { color: "#9e9e9e" }
                        }}
                    >
                        Previous
                    </Button>

                    {/* PAGE NUMBER BUTTONS */}
                    {[...Array(totalPage)].slice(0, visiblePageCount).map((_, idx) => {
                        const pageNum = idx + 1;
                        return (
                            <Button
                                key={pageNum}
                                onClick={() => setPage(pageNum)}
                                sx={{
                                    minWidth: "30px",
                                    height: "30px",
                                    fontSize: "12px",
                                    borderRadius: "6px",
                                    color: page === pageNum ? "#fff" : "#333",
                                    backgroundColor: page === pageNum ? "#007A99" : "#fff",
                                    border: "1px solid #d0d0d0",
                                    "&:hover": {
                                        backgroundColor: page === pageNum ? "#336CFB" : "#f2f2f2"
                                    }
                                }}
                            >
                                {pageNum}
                            </Button>
                        );
                    })}


                    {page > 5 &&
                        <Button
                            // onClick={() => setPage(pageNum)}
                            sx={{
                                minWidth: "30px",
                                height: "30px",
                                fontSize: "12px",
                                borderRadius: "6px",
                                color: "#fff",
                                backgroundColor: "#007A99",
                                border: "1px solid #d0d0d0",
                                "&:hover": {
                                    backgroundColor: "#007A99",
                                }
                            }}
                        >
                            {page}
                        </Button>
                    }



                    {/* Next Button */}
                    <Button
                        onClick={() => page < totalPage && setPage(page + 1)}
                        endIcon={<ArrowForwardIcon sx={{ fontSize: "14px" }} />}
                        disabled={page === totalPage}
                        sx={{
                            textTransform: "none",
                            fontSize: "12px",
                            color: page === totalPage ? "#9e9e9e" : "#007A99",
                            "&:disabled": { color: "#9e9e9e" }
                        }}
                    >
                        Next
                    </Button>

                    {/* Last Button */}
                    {page !== totalPage &&
                        <Button
                            onClick={() => setPage(totalPage)}
                            sx={{
                                textTransform: "none",
                                fontSize: "12px",
                                color: "#336CFB"
                            }}
                        // disabled={page === totalPage}
                        >
                            Last &gt;&gt;
                        </Button>
                    }

                    {/* First  Button */}

                    {page !== 1 &&
                        <Button
                            onClick={() => setPage(1)}
                            sx={{
                                textTransform: "none",
                                fontSize: "12px",
                                color: "#336CFB"
                            }}
                            disabled={page === 1}
                        >
                            &lt;&lt;  First
                        </Button>
                    }

                </Box>
            </Box>


            <ModalDelete deleteModal={deleteModal} setDeleteModal={setDeleteModal} handleDelete={handleDelete} id={id} label={'Treatment Template'} />
        </Box>
    )
}

export default TreatmentTemplate;

